import React, { useEffect, useState } from 'react';
import { fetchWithAuth } from '../util/fetchWithAuth';
import DashboardCard from '../components/dashboard/DashboardCard';
import SecureDashboardCard from '../components/dashboard/SecureDashboardCard';
import StoresSalesTrendChart from '../components/dashboard/StoresSalesTrendChart';
import CustomersTrendChart from '../components/dashboard/CustomersTrendChart';
import SalesSubmissionsTrendChart from '../components/dashboard/SalesSubmissionsTrendChart';
import OrderOriginsTrendChart from '../components/dashboard/OrderOriginsTrendChart';
import FullScreenLoader from '../components/FullScreenLoader';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showMonthlyIncome, setShowMonthlyIncome] = useState(false);
  const [showExtraIncome, setShowExtraIncome] = useState(false);
  const [showOrderProfit, setShowOrderProfit] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [refreshKey, setRefreshKey] = useState(0);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const response = await fetchWithAuth(`/dashboard?month=${selectedMonth}&year=${selectedYear}`);
        const data = await response.json();
        setMetrics(data.data);
      } catch (error) {
        console.error('Error fetching dashboard metrics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMetrics();
  }, [selectedMonth, selectedYear, refreshKey]);

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      const response = await fetchWithAuth(
        `/dashboard/download_order_report?month=${selectedMonth}&year=${selectedYear}`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const monthPadded = String(selectedMonth).padStart(2, '0');
        a.download = `order_report_${monthPadded}_${selectedYear}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Order report downloaded successfully');
      } else {
        toast.error('Failed to generate order report');
      }
    } catch (error) {
      console.error('Error generating order report:', error);
      toast.error('Error generating order report');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  if (isLoading) {
    return <FullScreenLoader message={'Fetching metrics...'} />;
  }

  if (!metrics) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <button
            onClick={() => {
              setIsLoading(true);
              setRefreshKey(prev => prev + 1);
            }}
          >
            <span className="material-icons text-3xl">refresh</span>
          </button>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-600">Failed to load dashboard metrics. Please try refreshing.</p>
        </div>
      </div>
    );
  }

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const getMonthName = (monthNumber: number) => {
    return months.find(m => m.value === monthNumber)?.label || '';
  };

  const payingCustomers = metrics.total_customers_with_converted_orders;
  const registeredCustomers = metrics.total_registered_customers;
  const registeredToPayingRatio =
    payingCustomers > 0 ? `${(registeredCustomers / payingCustomers).toFixed(1)} : 1` : '—';

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <button
          onClick={() => {
            setIsLoading(true);
            setRefreshKey(prev => prev + 1);
          }}
        >
          <span className="material-icons text-3xl">refresh</span>
        </button>
      </div>

      {/* Static metrics */}
      <div className="flex flex-wrap gap-4">
        <DashboardCard title="Active Orders" value={metrics.active_orders} />
        <DashboardCard title="Active Order Statuses">
          <ul className="text-sm text-gray-700 space-y-1">
            <li><span className="inline-block w-3 h-3 rounded-full bg-gray-600 mr-2"></span>Paid: {metrics.order_statuses?.paid}</li>
            <li><span className="inline-block w-3 h-3 rounded-full bg-red-400 mr-2"></span>Items Purchased: {metrics.order_statuses?.items_purchased}</li>
            <li><span className="inline-block w-3 h-3 rounded-full bg-yellow-300 mr-2"></span>Ready for Collection: {metrics.order_statuses?.ready_for_collection}</li>
          </ul>
        </DashboardCard>
        <DashboardCard
          title="Stuck Orders (17+ days)"
          info="Orders that have sat in their current status for 17 days or more without moving on. Each status is measured from when the order entered it. Click to see them."
          linkTo="/orders?stuck=true"
        >
          {/* One line per key in the backend's Order::STUCK_STATUS_CLOCKS. A status added
              there needs a line here too, or the card will quietly under-report. */}
          <ul className="text-sm text-gray-700 space-y-1">
            <li><span className="inline-block w-3 h-3 rounded-full bg-gray-600 mr-2"></span>Paid: {metrics.stuck_order_statuses?.paid}</li>
            <li><span className="inline-block w-3 h-3 rounded-full bg-red-400 mr-2"></span>Items Purchased: {metrics.stuck_order_statuses?.items_purchased}</li>
            <li><span className="inline-block w-3 h-3 rounded-full bg-yellow-300 mr-2"></span>Ready for Collection: {metrics.stuck_order_statuses?.ready_for_collection}</li>
          </ul>
        </DashboardCard>
        <DashboardCard title="Unpaid Extra Income" value={metrics.unpaid_extra_incomes} />
        <DashboardCard title="Unpaid Refunds" value={metrics.unpaid_refunds} />
        <DashboardCard title="Unconfirmed Order Costs" value={metrics.unconfirmed_costs_orders} />
        {user?.role === 'admin' && (
          <DashboardCard title="Loss-Making Orders" value={metrics.loss_making_orders} />
        )}
        <DashboardCard title="Failed Whatsapp Messages (24hrs)" value={metrics.failed_whatsapp_messages} />
        <DashboardCard title="Orders Without Exported Invoice (72hrs)" value={metrics.orders_without_exported_invoice} />
        <DashboardCard title="Customer Credit">
          <p className="text-3xl font-bold text-gray-900">{metrics.total_credit_balance}</p>
          <p className="text-sm text-gray-500">{metrics.customers_with_credit_count} customer{metrics.customers_with_credit_count !== 1 ? 's' : ''} with credit</p>
        </DashboardCard>
      </div>
      <button
        onClick={() => setShowCharts(prev => !prev)}
        className="mt-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <span className="material-icons text-base">{showCharts ? 'expand_less' : 'expand_more'}</span>
        {showCharts ? 'Hide charts' : 'Show charts'}
      </button>
      {showCharts && (
        <div className="mt-4 grid grid-cols-2 gap-4 mb-2">
          {metrics.stores_sales_trend && <StoresSalesTrendChart trend={metrics.stores_sales_trend} />}
          {metrics.customers_trend && <CustomersTrendChart data={metrics.customers_trend} />}
          {metrics.sales_submissions_trend && <SalesSubmissionsTrendChart data={metrics.sales_submissions_trend} />}
          {metrics.order_origins_trend && <OrderOriginsTrendChart data={metrics.order_origins_trend} />}
        </div>
      )}

      <hr className="my-6 border-gray-300" />

      {/* Time-based metrics */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-1xl font-bold text-gray-800">{getMonthName(selectedMonth)} {selectedYear} Stats</h3>
        <div className="flex items-center gap-2">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-2 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-2 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          {user?.role === 'admin' && (
            <button
              onClick={handleGenerateReport}
              disabled={isGeneratingReport}
              className="px-2 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center text-base"
              title="Download Order Report"
            >
              {isGeneratingReport ? (
                <span className="material-icons animate-spin">refresh</span>
              ) : (
                <span className="material-icons">download</span>
              )}
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mb-6">
        <DashboardCard title="Total Sales" value={metrics.orders_paid_for} />
        <DashboardCard title="Total Order Form Submissions" value={metrics.orders_submitted} />
        <DashboardCard title="Order Origins">
          <ul className="text-sm text-gray-700 space-y-1">
            <li><span className="inline-block w-3 h-3 rounded-full bg-green-200 mr-2"></span>WhatsApp: {metrics.orders_from_whatsapp}</li>
            <li><span className="inline-block w-3 h-3 rounded-full bg-blue-200 mr-2"></span>Website: {metrics.orders_from_website}</li>
            <li><span className="inline-block w-3 h-3 rounded-full bg-purple-200 mr-2"></span>App: {metrics.orders_from_app}</li>
          </ul>
        </DashboardCard>
        <DashboardCard title="Customer Origins">
          <ul className="text-sm text-gray-700 space-y-1">
            <li><span className="inline-block w-3 h-3 rounded-full bg-green-200 mr-2"></span>WhatsApp: {metrics.customers_from_whatsapp}</li>
            <li><span className="inline-block w-3 h-3 rounded-full bg-blue-200 mr-2"></span>Website: {metrics.customers_from_website}</li>
            <li><span className="inline-block w-3 h-3 rounded-full bg-purple-200 mr-2"></span>App: {metrics.customers_from_app}</li>
          </ul>
        </DashboardCard>
        <DashboardCard title="New Customers" value={metrics.new_customers} />
        <DashboardCard title="Returning Customer Orders" value={metrics.returning_customers} />
        <DashboardCard title="Cancelled Orders" value={metrics.cancelled_orders} />
        <DashboardCard title="App Sign Ups Without Orders" value={metrics.app_signups_without_orders} />
        <DashboardCard title="Unverified Customers" value={metrics.unverified_customers} />
        {user?.role === 'admin' && metrics.monthly_delivery_income && (
          <SecureDashboardCard
            title="Monthly Delivery Income"
            value={metrics.monthly_delivery_income}
            isVisible={showMonthlyIncome}
            onReveal={() => setShowMonthlyIncome(!showMonthlyIncome)}
          />
        )}
        {user?.role === 'admin' && metrics.monthly_extra_income && (
          <SecureDashboardCard
            title="Monthly Extra Income"
            value={metrics.monthly_extra_income}
            isVisible={showExtraIncome}
            onReveal={() => setShowExtraIncome(!showExtraIncome)}
          />
        )}
        {user?.role === 'admin' && metrics.monthly_order_profit && (
          <SecureDashboardCard
            title="Order Cost Profit"
            value={metrics.monthly_order_profit}
            isVisible={showOrderProfit}
            onReveal={() => setShowOrderProfit(!showOrderProfit)}
          />
        )}
        {/* <DashboardCard title="Delivery Income" value={metrics.total_delivery_income} /> */}
        {/* <DashboardCard title="Extra Income" value={metrics.total_extra_income} /> */}

      </div>

      <hr className="my-6 border-gray-300" />

      {/* All Time Stats */}
      <h3 className="text-1xl font-bold text-gray-800">All Time Stats</h3>
      <div className="flex flex-wrap gap-4 mb-6">
        <DashboardCard title="Registered Customers" value={metrics.total_registered_customers} />
        <DashboardCard title="Total Sales" value={metrics.total_converted_orders} />
        <DashboardCard
          title="Paying Customers"
          value={metrics.total_customers_with_converted_orders}
          info="Customers with at least one completed (non-submitted) order."
        />
        <DashboardCard
          title="Registered : Paying Ratio"
          value={registeredToPayingRatio}
          info="Registered customers for every paying customer. Lower is better — a ratio closer to 1:1 means more of the people who register go on to place an order."
        />
        <DashboardCard
          title="App-Verified Customers"
          value={metrics.total_verified_customers}
          info="Customers who verified their number via the app's WhatsApp OTP login. Excludes WhatsApp-only and admin-created customers who never verified."
        />
      </div>
      
      <br/>
      <br/>
    </div>
  );
}

export default Dashboard;
